import type { ReactElement, ReactNode } from "react"
import { Link } from "react-router"
import styled, { css } from "styled-components"
import { useAccessWindow } from "~/features/entitlements/AccessWindowContext"
import { accessBadge } from "~/features/entitlements/accessBadge"
import { AccessBadge } from "~/features/entitlements/components/AccessBadge"
import { usePocOptions } from "~/features/poc"
import { Provenance } from "~/features/provenance"
import { useStudyEbook } from "~/features/studyEbook/StudyEbookContext"
import { ministryLabel } from "~/shared/theme/audience"
import { type SectionStyle, toneFor } from "~/shared/theme/tokens"
import { BookLink, Card, ManageAccess, RemoteImage } from "~/shared/ui"
import type { LibraryCardItem } from "../types"

const linkStyles = css`
  display: block;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radius.md};

  &:hover h3 {
    color: ${({ theme }) => theme.color.linkHover};
    text-decoration: underline;
  }
`

const CardLink = styled(Link)`
  ${linkStyles}
`

const ExternalCardLink = styled.a`
  ${linkStyles}
`

const Row = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

/**
 * A card with nothing behind it. Dimmed and desaturated rather than hidden, so
 * the customer can still see what they had — which is the whole reason these are
 * on the page — while it reads as unavailable at a glance down the grid.
 *
 * The pill is deliberately exempt from the dimming: `Expired 5/1/26` is the one
 * thing on the card that must stay legible.
 */
const Lapsed = styled.div`
  cursor: default;

  img {
    filter: grayscale(1);
    opacity: 0.55;
  }

  h3,
  p {
    color: ${({ theme }) => theme.color.textMuted};
  }
`

/**
 * A header icon, padded to a comfortable hit target. A sibling of the card
 * link, never a descendant: the card body is already one anchor, and nesting a
 * second inside it is invalid HTML that browsers repair unpredictably.
 */
const IconLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxs};
  color: inherit;

  &:hover {
    opacity: 0.8;
  }
`

interface LibraryCardProps {
  readonly item: LibraryCardItem
  /** How the section draws its cards: the artwork well, and the tone an unplaced card wears. */
  readonly section: SectionStyle
  /**
   * Whether to look for a companion book. Only the Studies section asks: every
   * other section's cards would either match nothing or, in Ebooks, match
   * themselves.
   */
  readonly showMaterial?: boolean
  readonly qaHook: string
}

export const LibraryCard = ({ item, section, showMaterial = false, qaHook }: LibraryCardProps): ReactElement => {
  /**
   * POC. `libraryV2` serves no seat and no join key, so the window is matched
   * back from the commerce rows by title — see `entitlements/windows.ts` for why
   * that is a heuristic and what it refuses to guess. Undefined outside the POC
   * provider, in which case the card renders exactly as it did before.
   *
   * Deliberately annotation only: it never gates the link. An expired seat still
   * links through, because a title match is not strong enough evidence to take
   * access away, and `libraryV2` returning the item at all is the real
   * entitlement signal.
   *
   * A card carrying its own window is the exception, and not a contradiction: it
   * came FROM a seat rather than from `libraryV2` (`orphanEntitlements.ts`), so
   * there is no title match involved and no asset to link to either way.
   */
  /**
   * By title, like the window above and for the same reason: the graph carries no
   * link between a study and its book. See `features/studyEbook/studyEbookIndex.ts`.
   */
  const pairedMaterial = useStudyEbook(showMaterial ? item.title : undefined)

  /** POC. Off strips the tinted block and the header icons — see `Card`'s `plain`. */
  const { cardColor } = usePocOptions()

  const matchedWindow = useAccessWindow(item.title)
  const accessWindow = item.accessWindow ?? matchedWindow

  /**
   * The card is the DCE graph's; the pill on it is myLifeway's. That split is the
   * overlay's whole point — an active Studies card carrying an `Until 6/30/27`
   * pill is one card assembled from two systems, and it looks like one field.
   * A card built from a seat (`orphanEntitlements.ts`) is myLifeway's throughout.
   */
  const cardSource = item.accessWindow ? "mlc" : "dce"

  /**
   * The badge decision is made here rather than left inside `AccessBadge` so the
   * empty case is a `null` child the overlay can see: `AccessBadge` returning
   * null looks like a rendered element from the outside, and the overlay must
   * not outline a slot that drew nothing.
   */
  const hasBadge = accessBadge(accessWindow) !== undefined

  const materialLink = pairedMaterial ? (
    <Provenance source={pairedMaterial.source} inline>
      <IconLink
        href={pairedMaterial.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${pairedMaterial.title} (opens in Lifeway Reader)`}
        data-qa-hook={`${qaHook}-material`}
        data-reader-id-kind={pairedMaterial.linkIdKind}
      >
        <BookLink />
      </IconLink>
    </Provenance>
  ) : null

  const manageLink = accessWindow?.manageHref ? (
    <IconLink
      href={accessWindow.manageHref}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Manage access for ${item.title}`}
      data-qa-hook={`${qaHook}-manage`}
    >
      <ManageAccess />
    </IconLink>
  ) : null

  const action =
    materialLink || manageLink ? (
      <>
        {materialLink}
        {manageLink}
      </>
    ) : undefined

  const href = item.href

  /**
   * Undefined leaves the poster, title and meta unwrapped — a lapsed seat with
   * no library row behind it has nothing to link to.
   */
  const bodyLink = href
    ? item.isExternal
      ? (linkBody: ReactNode): ReactElement => (
          <ExternalCardLink
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${item.title} (opens in Lifeway Reader)`}
            data-qa-hook={`${qaHook}-link`}
            /* Which identifier the reader was handed — the ebook link-mode
               toggle's only visible evidence that a card fell back. */
            data-reader-id-kind={item.linkIdKind}
          >
            {linkBody}
          </ExternalCardLink>
        )
      : (linkBody: ReactNode): ReactElement => (
          <CardLink to={href} aria-label={item.title} data-qa-hook={`${qaHook}-link`}>
            {linkBody}
          </CardLink>
        )
    : undefined

  const card = (
    <Card
      qaHook={qaHook}
      title={item.title}
      tone={toneFor(item.audience, section.tone)}
      plain={!cardColor}
      label={ministryLabel(item.audience)}
      action={action}
      badge={
        <Provenance source="mlc" inline>
          {hasBadge ? <AccessBadge accessWindow={accessWindow} qaHook={`${qaHook}-badge`} /> : null}
        </Provenance>
      }
      chip={item.dateChip}
      media={
        <RemoteImage
          src={item.imageUrl}
          alt=""
          ratio={section.artwork.ratio}
          fit={section.artwork.fit}
          qaHook={`${qaHook}-image`}
        />
      }
      hasCoverArt={Boolean(item.imageUrl)}
      bodyLink={bodyLink}
    />
  )

  if (!href) {
    return (
      <Row>
        <Provenance source={cardSource}>
          <Lapsed data-qa-hook={`${qaHook}-lapsed`} aria-disabled="true">
            {card}
          </Lapsed>
        </Provenance>
      </Row>
    )
  }

  return (
    <Row>
      <Provenance source={cardSource}>{card}</Provenance>
    </Row>
  )
}
